import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Info, Diamond as DiamondIcon, ExternalLink } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const DiamondDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [diamond, setDiamond] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  const fetchDiamondDetails = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/diamonds/getDiamonds?diamond_id=${id}`);
      if (resp.data && resp.data.success && resp.data.data.length > 0) {
        setDiamond(resp.data.data[0]);
      }
    } catch (err) {
      console.error('Error fetching diamond details:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDiamondDetails();
  }, [fetchDiamondDetails]);

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!diamond) {
    return (
      <Container className="py-5 mt-5 text-center">
        <h2>{t('diamondDetails.notFound')}</h2>
        <Button variant="link" onClick={() => navigate('/diamonds')}>{t('common.back')} to {t('common.search')}</Button>
      </Container>
    );
  }

  const mediaList = [];
  if (diamond.image_link) mediaList.push({ type: 'image', url: diamond.image_link });
  if (diamond.video_link) mediaList.push({ type: 'iframe', url: diamond.video_link });

  const ImageViewer = ({ url }) => (
    <img 
      src={url} 
      className="w-100 img-fluid object-fit-contain" 
      style={{ maxHeight: '100%', maxWidth: '100%' }}
      alt="Diamond" 
    />
  );

  const IframeViewer = ({ url }) => (
    <div className="w-100 h-100">
      <iframe 
        src={url} 
        title="Diamond 360 Viewer"
        className="w-100 h-100 border-0"
        allowFullScreen
      />
    </div>
  );

  const MediaRenderer = ({ media }) => {
    switch (media.type) {
      case 'image': return <ImageViewer url={media.url} />;
      case 'iframe': return <IframeViewer url={media.url} />;
      default: return <div className="text-center opacity-25 p-5"><DiamondIcon size={120} /><div className="mt-2 fw-bold">{t('diamondDetails.noMedia')}</div></div>;
    }
  };

  return (
    <section className="py-5 mt-5 sunflower">
      <Container>
        <Button 
          variant="link" 
          className="text-decoration-none p-0 mb-4 d-flex align-items-center gap-1 text-muted Sunflower"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft size={16} /> {t('diamondDetails.backToResults')}
        </Button>

        <Row className="g-5">
          {/* Left Column: Gallery */}
          <Col lg={7} data-aos="fade-right">
            <Row className="g-3">
              <Col md={2} className="order-2 order-md-1">
                <div className="d-flex flex-md-column gap-3">
                  {mediaList.map((media, idx) => (
                    <div 
                      key={idx} 
                      className={`detail-thumb ${activeImg === idx ? 'active' : ''}`}
                      onClick={() => setActiveImg(idx)}
                    >
                      {media.type === 'image' ? (
                        <img src={media.url} className="w-100 h-100 object-fit-cover" alt="Thumb" />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-dark text-white text-center" style={{ fontSize: '10px' }}>360° VIEW</div>
                      )}
                    </div>
                  ))}
                  {mediaList.length === 0 && (
                    <div className="detail-thumb active d-flex align-items-center justify-content-center text-muted">
                      <DiamondIcon size={32} />
                    </div>
                  )}
                </div>
              </Col>
              <Col md={10} className="order-1 order-md-2">
                <div className="detail-main-img-container shadow-sm border position-relative">
                  {mediaList.length > 0 ? (
                    <MediaRenderer media={mediaList[activeImg]} />
                  ) : (
                    <div className="text-center opacity-25 p-5">
                       <DiamondIcon size={120} />
                       <div className="mt-2 fw-bold">{t('diamondDetails.noMedia')}</div>
                    </div>
                  )}
                  {diamond.source === 'lab grown' && (
                    <Badge bg="info" className="position-absolute top-0 start-0 m-4 px-3 py-2 text-uppercase sunflower" style={{ letterSpacing: '1px' }}>
                      {t('common.labGrown')}
                    </Badge>
                  )}
                </div>
                <div className="mt-3 d-flex justify-content-center">
                   {diamond.certificate_link && (
                     <a href={diamond.certificate_link} target="_blank" rel="noopener noreferrer" className="text-decoration-none d-flex align-items-center gap-2 text-muted small">
                        <ExternalLink size={14} /> {t('diamondDetails.viewCertificate')} ({diamond.certificate_inst})
                     </a>
                   )}
                </div>
              </Col>
            </Row>
          </Col>

          {/* Right Column: Specs & Buy */}
          <Col lg={5} data-aos="fade-left" data-aos-delay="200">
            <div className="pd-sticky-top" style={{ position: 'sticky', top: '100px' }}>
              <div className="mb-4">
                <h6 className="text-uppercase text-muted small mb-2 sunflower" style={{ letterSpacing: '2px' }}>
                   {diamond.source === 'lab grown' ? t('common.labGrown') : t('common.natural')} {t('common.diamonds')}
                </h6>
                <h1 className="fw-bold mb-3" style={{ fontFamily: 'var(--font-serif)', fontSize: '2.5rem' }}>
                  {diamond.carat} {t('diamondDetails.specs.carat')} {diamond.cut}
                </h1>
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div className="premium-price">€ {diamond.price || '--'}</div>
                  <Badge bg="light" text="dark" className="border">{diamond.price ? t('common.available') : t('common.priceOnRequest')}</Badge>
                </div>
                
                <a 
                  className="btn btn-primary w-100 py-3 mb-3 fw-bold sunflower d-flex align-items-center justify-content-center gap-2" 
                  style={{ border: 'none', borderRadius: '4px' }}
                  href={`https://api.whatsapp.com/send/?phone=+4915202693620&text=Hi%20I'm%20interested%20in%20Diamond%20SKU:%20${diamond.sku || 'N/A'}&type=phone_number&app_absent=0`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                   <FaWhatsapp size={20} /> {t('diamondDetails.contactCta')}
                </a>
              </div>

              <div className="mt-5">
                <h5 className="mb-4 fw-bold sunflower">{t('diamondDetails.technicalSpecs')}</h5>
                <div className="spec-table shadow-sm p-4 rounded bg-white border">
                  <SpecRow label={t('diamondDetails.specs.sku')} value={diamond.sku} />
                  <SpecRow label={t('diamondDetails.specs.shape')} value={diamond.cut} />
                  <SpecRow label={t('diamondDetails.specs.carat')} value={`${diamond.carat} ct`} />
                  {diamond.color && <SpecRow label={t('diamondDetails.specs.color')} value={diamond.color} />}
                  {diamond.fancyColor && <SpecRow label={t('diamondDetails.specs.fancyColor')} value={diamond.fancyColor} />}
                  <SpecRow label={t('diamondDetails.specs.clarity')} value={diamond.clarity} />
                  <SpecRow label={t('diamondDetails.specs.polish')} value={diamond.polish} />
                  <SpecRow label={t('diamondDetails.specs.symmetry')} value={diamond.symmetry} />
                  <SpecRow label={t('diamondDetails.specs.fluorescence')} value={diamond.fluorescence} />
                  <SpecRow label={t('diamondDetails.specs.measurements')} value={diamond.measurements} />
                  <SpecRow label={t('diamondDetails.specs.ratio')} value={diamond.lw_ratio} />
                  <SpecRow label={t('diamondDetails.specs.table')} value={diamond.table_width ? `${diamond.table_width}%` : '-'} />
                  <SpecRow label={t('diamondDetails.specs.depth')} value={diamond.depth ? `${diamond.depth}%` : (diamond.total_depth ? `${diamond.total_depth}%` : '-')} />
                  <SpecRow label={t('diamondDetails.specs.color')} value={diamond.color} />
                  <SpecRow label={t('diamondDetails.specs.fancyColor')} value={diamond.fancy_color} />
                  <SpecRow label={t('diamondDetails.specs.fancyIntensity')} value={diamond.fancy_color_inte} />
                  <SpecRow label={t('diamondDetails.specs.source')} value={diamond.source === 'lab grown' ? t('common.labGrown') : t('common.natural')} />
                  <SpecRow 
                    label={t('diamondDetails.specs.certNum')} 
                    value={
                      diamond.certificate_link && diamond.certificate_num ? (
                        <a href={diamond.certificate_link} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none fw-medium">
                          {diamond.certificate_num} <ExternalLink size={12} className="ms-1" />
                        </a>
                      ) : (
                        diamond.certificate_num
                      )
                    } 
                  />
                  <SpecRow label={t('diamondDetails.specs.institute')} value={diamond.certificate_inst} />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

const SpecRow = ({ label, value }) => (
  <div className="spec-row">
    <div className="spec-label">
      <Info size={14} className="opacity-50" />
      {label}
    </div>
    <div className="spec-value">{value || '-'}</div>
  </div>
);

export default DiamondDetails;
